import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

export const loadAccountsByUser = createAsyncThunk(
    'accounts/loadAccountsByUser',
    async ({username, token}) => {
        const response = await fetch(`${process.env.REACT_APP_BASE_URL}/user/accounts/user/${username}`,
            {
                method: 'GET',
                headers: {
                    "Authorization": `Bearer ${token}`
                }
            }
        )
        .then(response => response.json());
        return response;
    }
)

export const loadAccountsByBank = createAsyncThunk(
    'accounts/loadAccountsByBank',
    async ({bankId, token}) => {
        const response = await fetch(`${process.env.REACT_APP_BASE_URL}/teller/accounts/bank/${bankId}`,
            {
                method: 'GET',
                headers: {
                    "Authorization": `Bearer ${token}`
                }
            }
        )
        .then(response => response.json());
        return response;
    }
)

export const loadAllAccounts = createAsyncThunk(
    'accounts/loadAllAccounts',
    async(token) => {
        const response = await fetch(`${process.env.REACT_APP_BASE_URL}/admin/accounts`,
            {
                method: 'GET',
                headers: {
                    "Authorization": `Bearer ${token}`
                }
            }
        )
        .then(response => response.json());
        return response;
    }
)

export const deleteAccount = createAsyncThunk(
    'accounts/deleteAccount',
    async ({accountId, token}) => {
        const response = await fetch(`${process.env.REACT_APP_BASE_URL}/admin/accounts/${accountId}/delete`,
            {
                method: 'DELETE',
                headers: {
                    "Authorization": `Bearer ${token}`
                }
            }
        )
        .then(response => response.json());
        return response;
    }
)

export const accountsSlice = createSlice({
    name: 'accounts',
    initialState: {
        accountsLoading: false,
        failedToLoadAccounts: false,
        accountIdToDelete: '',
        confirmingDeleteAccount: false,
        accountDeleting: false,
        failedToDeleteAccount: false,
        accounts: {},
        selectedBranch: "blank",
        accountsCheckBoxes: []
    },
    reducers: {
        changeSelectedBranch: (state, action) => {
            return {
                ...state,
                selectedBranch: action.payload
            }
        },
        addAccount: (state, action) => {
            return {
                ...state,
                accounts: {
                    ...state.accounts,
                    [action.payload.accountId]: action.payload
                }
            }
        },
        removeAccount: (state, action) => {
            let newAccountList = {};
            Object.values(state.accounts).forEach(account => {
                if (account.accountId !== action.payload) {
                    newAccountList = {
                        ...newAccountList,
                        [account.accountId]: account
                    }
                }
            })
            return {
                ...state,
                accounts: newAccountList,
                accountIdToDelete: ''
            }
        },
        clearAccounts: (state) => {
            return {
                ...state,
                accounts: {}
            }
        },
        setConfirmingDeleteAccount: (state, action) => {
            return {
                ...state,
                confirmingDeleteAccount: action.payload
            }
        },
        changeAccountIdToDelete: (state, action) => {
            return {
                ...state,
                accountIdToDelete: action.payload
            }
        },
        setAccountsCheckboxes: (state, action) => {
            return {
                ...state,
                accountsCheckBoxes: action.payload
            }
        }
    },
    extraReducers: {
        [loadAccountsByUser.pending]: (state) => {
            return {
                ...state,
                accountsLoading: true,
                failedToLoadAccounts: false
            }
        },
        [loadAccountsByUser.fulfilled]: (state, action) => {
            const newAccounts = {};
            action.payload.forEach(account => {
                newAccounts[account.accountId] = {
                    username: account.username,
                    bankId: account.bankId,
                    branchId: account.branchId,
                    accountId: account.accountId,
                    accountType: account.accountType,
                    balance: account.balance,
                    active: account.active
                }
            })
            return {
                ...state,
                accountsLoading: false,
                failedToLoadAccounts: false,
                accounts: newAccounts
            }
        },
        [loadAccountsByUser.rejected]: (state) => {
            return {
                ...state,
                accountsLoading: false,
                failedToLoadAccounts: false,
            }
        },
        [loadAccountsByBank.pending]: (state) => {
            return {
                ...state,
                accountsLoading: true,
                failedToLoadAccounts: false
            }
        },
        [loadAccountsByBank.fulfilled]: (state, action) => {
            const newAccounts = {};
            action.payload.forEach(account => {
                newAccounts[account.accountId] = {
                    username: account.username,
                    bankId: account.bankId,
                    branchId: account.branchId,
                    accountId: account.accountId,
                    accountType: account.accountType,
                    balance: account.balance,
                    active: account.active
                }
            })
            return {
                ...state,
                accountsLoading: false,
                failedToLoadAccounts: false,
                accounts: newAccounts
            }
        },
        [loadAccountsByBank.rejected]: (state) => {
            return {
                ...state,
                accountsLoading: false,
                failedToLoadAccounts: true
            }
        },
        [loadAllAccounts.pending]: (state) => {
            return {
                ...state,
                accountsLoading: true,
                failedToLoadAccounts: false
            }
        },
        [loadAllAccounts.fulfilled]: (state, action) => {
            const newAccounts = {};
            action.payload.forEach(account => {
                newAccounts[account.accountId] = {
                    username: account.username,
                    bankId: account.bankId,
                    branchId: account.branchId,
                    accountId: account.accountId,
                    accountType: account.accountType,
                    balance: account.balance,
                    active: account.active
                }
            })
            return {
                ...state,
                accountsLoading: false,
                failedToLoadAccounts: false,
                accounts: newAccounts
            }
        },
        [loadAllAccounts.rejected]: (state) => {
            return {
                ...state,
                accountsLoading: false,
                failedToLoadAccounts: true
            }
        },
        [deleteAccount.pending]: (state) => {
            return {
                ...state,
                accountDeleting: true,
                failedToDeleteAccount: false
            }
        },
        [deleteAccount.fulfilled]: (state) => {
            return {
                ...state,
                accountDeleting: false,
                failedToDeleteAccount: false
            }
        },
        [deleteAccount.rejected]: (state) => {
            return {
                ...state,
                accountDeleting: false,
                failedToDeleteAccount: true
            }
        }
    }
})

export const selectAccounts = (state) => state.accounts.accounts;
export const accountsLoading = (state) => state.accounts.accountsLoading;
export const selectCurrentBranch = (state) => state.accounts.selectedBranch;
export const selectAccountIdToDelete = (state) => state.accounts.accountIdToDelete;
export const selectConfirmingDeleteAccount = (state) => state.accounts.confirmingDeleteAccount;
export const selectAccountsCheckboxes = (state) => state.accounts.accountsCheckBoxes;

export const { 
    changeSelectedBranch, 
    clearAccounts, 
    setConfirmingDeleteAccount, 
    changeAccountIdToDelete, 
    addAccount, 
    removeAccount,
    setAccountsCheckboxes
} = accountsSlice.actions;

export default accountsSlice.reducer;